/**
 * `loadWidgetConfig` — fetch the public, unauthenticated widget configuration
 * (theme + pre-chat form + welcome/offline copy + online flag) for an embed id
 * (ARCHITECTURE §7).
 *
 * Uses the shared API client's public surface (`api.widget.config(cid)`,
 * `endpoints.widgetPublic.config`). This is the FIRST network call the app makes,
 * before any session exists, so it runs anonymously.
 */

import { createApiClient, type ApiClient } from '@flozic/shared/api';
import type { WidgetConfig } from '@flozic/shared/types';
import { visitorStore } from '../session/visitorStore';
import { widgetEnv } from './env';

/** Backend REST origin (dev-default localhost:8000; see `config/env.ts`). */
const API_BASE_URL: string = widgetEnv.apiBaseUrl;

/**
 * The public config payload as returned by `GET /public/widget/{cid}/config`
 * (backend `WidgetBootstrapOut`). It is the shared `WidgetConfig` view plus the
 * live `online` flag the widget uses to pick the pre-chat vs offline screen.
 * (The public endpoint omits `publicKey`/`allowedOrigins`, which the anonymous
 * client never needs.)
 */
// The bootstrap is the shared WidgetConfig view plus the live `online` flag. The public endpoint
// serves only ENABLED, TRIMMED triggers (no admin id/name — see backend PublicTriggerConfig), so
// the `triggers` here carry no id/name at runtime even though the shared type lists them; the
// widget only ever reads enabled/message/condition (see useTriggers), so that's harmless.
export type WidgetBootstrap = WidgetConfig & { online: boolean; voiceEnabled: boolean };

/**
 * A single shared API client for the widget. The widget only ever uses the
 * public surface; authenticated calls (`getSession`, `send`) carry the opaque
 * session token via `getToken`, which reads the live token out of
 * `visitorStore`. The anonymous calls (`config`, `createSession`) pass
 * `anonymous: true` at the resource level, so no token is attached to them.
 */
let client: ApiClient | null = null;

/** Lazily build (and memoize) the widget's API client. */
export function getWidgetApi(): ApiClient {
  if (!client) {
    client = createApiClient({
      baseUrl: API_BASE_URL,
      getToken: () => visitorStore.getSnapshot().sessionToken,
    });
  }
  return client;
}

/**
 * Load the public widget config for the given `cid` (`ORG_ID-WIDGET_ID`).
 *
 * Anonymous (no session yet). Returns theme + pre-chat form + welcome/offline
 * copy + the `online` flag.
 */
export async function loadWidgetConfig(cid: string): Promise<WidgetBootstrap> {
  const api = getWidgetApi();
  const raw = (await api.widget.config(cid)) as WidgetConfig & {
    online?: boolean;
    voiceEnabled?: boolean;
  };
  return { ...raw, online: raw.online ?? false, voiceEnabled: raw.voiceEnabled ?? false };
}
